import { type ItineraryDetail } from '@utazzo/shared';
import { UzAlert, UzBadge, UzButton, UzDialog, UzPopover } from '@utazzo/ui';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  CreditCard,
  GitBranchPlus,
  Link2,
  Loader2,
  Lock,
  MoreHorizontal,
  QrCode,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  Link,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { ConfigureStep } from '../components/ConfigureStep';
import { DaysTab } from '../components/DaysTab';
import { InclusionsTab } from '../components/InclusionsTab';
import { LogsTab } from '../components/LogsTab';
import { PreviewTab } from '../components/PreviewTab';
import { PriceVisibilityProvider } from '../components/price-visibility';
import { PricingStep } from '../components/PricingStep';
import { RequestVoidButton } from '../components/RequestVoidButton';
import { VersionDiffTab } from '../components/VersionDiffTab';
import {
  useCreateItineraryVersion,
  useItineraryDays,
  useItineraryDetail,
  useItineraryVersions,
} from '../use-itineraries';

import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * Itinerary builder - a guided, gated stepper (not free tabs):
 *   1. Configure  - trip basics + destinations(+nights) + rooms -> generates days
 *   2. Days       - the auto-generated day-by-day plan; add hotels/flights/etc.
 *   3. Inclusions
 *   4. Pricing    - live quote rollup
 *   5. Preview
 *   6. Changes    - vs previous version (only when parentItineraryId is set)
 * Steps after Configure stay locked until the plan is generated (days exist),
 * except Changes which is available whenever this row has a parent version.
 */

type StepKey = 'configure' | 'changes' | 'days' | 'inclusions' | 'pricing' | 'preview' | 'logs';

const STATUS_META: Record<ItineraryDetail['status'], { dot: string; label: string }> = {
  DRAFT: { dot: 'bg-zinc-400', label: 'Draft' },
  IN_PROGRESS: { dot: 'bg-amber-500', label: 'In progress' },
  SENT_TO_CUSTOMER: { dot: 'bg-sky-500', label: 'Sent to customer' },
  REVISED: { dot: 'bg-amber-500', label: 'Revised' },
  ACCEPTED: { dot: 'bg-emerald-500', label: 'Accepted' },
  REJECTED: { dot: 'bg-red-500', label: 'Rejected' },
  CONVERTED_TO_BOOKING: { dot: 'bg-emerald-500', label: 'Converted to booking' },
  CANCELLED: { dot: 'bg-zinc-400', label: 'Cancelled' },
};

const KIND_LABEL: Record<ItineraryDetail['kind'], string> = {
  // FIT = Flexible Itinerary. Enum value stays CUSTOM; only the label changed.
  CUSTOM: 'FIT',
  BRIEF: 'Brief',
  HOLIDAY: 'Holiday',
  FIXED_DEPARTURE: 'Fixed departure',
};

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'configure', label: 'Configure' },
  { key: 'days', label: 'Days' },
  { key: 'inclusions', label: 'Inclusions' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'preview', label: 'Preview' },
  { key: 'changes', label: 'Changes' },
  { key: 'logs', label: 'Logs' },
];

const fmtDate = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '--';

/** Date + time, e.g. "22 Jul 2026, 11:32" - for the version dropdown so each
 *  version shows when it was published, not just the day. */
const fmtDateTime = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--';

const fmtPaise = (paise: string, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(paise) / 100);
  } catch {
    return `${currency} ${Math.round(Number(paise) / 100).toLocaleString('en-IN')}`;
  }
};

export const ItineraryDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  // `?from=lead|list` is set by the lead's "Create itinerary" button (and the
  // post-create navigate inside the draft configurator) so the back link
  // routes back to where the operator came from. Default = list.
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') === 'lead' ? 'lead' : 'list';
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<StepKey>('configure');
  const didInit = useRef(false);

  // Reset to Configure on version switch. If the navigation carried
  // `state.freshVersion === true` (thrown by the New Version dialog) the
  // operator ALWAYS starts on Configure - kill the auto "land on Days when
  // the plan is generated" jump for this session by pre-marking init as done.
  useEffect(() => {
    setStep('configure');
    const freshVersion =
      typeof location.state === 'object' &&
      location.state !== null &&
      (location.state as { freshVersion?: boolean }).freshVersion === true;
    didInit.current = freshVersion;
  }, [id, location.state]);

  const detailQuery = useItineraryDetail(id ?? null);
  const itinerary = detailQuery.data ?? null;
  const daysQuery = useItineraryDays(id ?? null);
  const versionsQuery = useItineraryVersions(itinerary?.leadId ?? null);
  const versions = versionsQuery.data ?? [];
  const createVersion = useCreateItineraryVersion();
  const me = useCurrentUser();
  // Changes tab (version diff) is admin-only — sales lead/agent don't see it.
  const canSeeChanges = me?.role === 'ADMIN';

  // Bounce off Changes when this row has no parent (v1), or when the
  // operator isn't allowed to see the tab at all.
  useEffect(() => {
    if (step !== 'changes') return;
    if (!canSeeChanges || (itinerary && !itinerary.parentItineraryId)) {
      setStep('configure');
    }
  }, [step, itinerary, canSeeChanges]);

  // Default to LATEST version on entry. If the URL points to an older row
  // on this lead AND we didn't just create/switch versions explicitly
  // (freshVersion state flag, set by New Version / Revise / dropdown), auto-
  // navigate to the head of the chain. Prevents users landing on stale
  // bookmarks + keeps the dropdown/list "always on the last one" behaviour.
  useEffect(() => {
    if (!itinerary || versions.length === 0) return;
    let latest = versions[0];
    if (!latest) return;
    for (const v of versions) if (v.version > latest.version) latest = v;
    if (latest.itineraryId === itinerary.id) return; // already on latest
    // Respect explicit user intent: dropdown click OR fresh-version create
    // pass `state.freshVersion=true` so we don't fight the navigation.
    const explicit =
      typeof location.state === 'object' &&
      location.state !== null &&
      (location.state as { freshVersion?: boolean }).freshVersion === true;
    if (explicit) return;
    navigate(`/itineraries/${latest.itineraryId}`, { replace: true });
  }, [itinerary, versions, location.state, navigate]);

  const copyToClipboard = (text: string, message: string): void => {
    void navigator.clipboard?.writeText(text);
    toast.success(message);
  };
  const copyVersionLink = (shareUrl: string): void => copyToClipboard(shareUrl, 'Link copied');

  // "New version" opens a blank-vs-copy-forward choice instead of a single
  // window.confirm; either choice fires the same create-version mutation +
  // post-create navigation, just with a different copyForward flag.
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  // Controlled popover state so a version-row click can close it programmatically
  // (default click-through leaves it open, so the user sees no reaction until the
  // main content skeleton appears - which reads as "nothing happened"). Always
  // starts closed and force-closes on every URL change so a version switch
  // (or landing from any other page) lands with a clean, closed dropdown -
  // matches native dropdown semantics.
  const [versionsOpen, setVersionsOpen] = useState(false);
  useEffect(() => setVersionsOpen(false), [id]);
  // Target itinerary id while a version switch is navigating. Used to render a
  // spinner on the tapped row and dim the popover so the click is visibly
  // acknowledged even before the URL update resolves.
  const [switchingToId, setSwitchingToId] = useState<string | null>(null);
  useEffect(() => {
    if (switchingToId && itinerary?.id === switchingToId) setSwitchingToId(null);
  }, [itinerary?.id, switchingToId]);
  const handleNewVersion = (): void => {
    if (!itinerary) return;
    setNewVersionDialogOpen(true);
  };
  const startNewVersion = (copyForward: boolean): void => {
    if (!itinerary) return;
    createVersion.mutate(
      { id: itinerary.id, copyForward },
      {
        onSuccess: (next) => {
          setNewVersionDialogOpen(false);
          navigate(`/itineraries/${next.id}`, { state: { freshVersion: true } });
        },
      },
    );
  };
  const hasDays = (daysQuery.data?.length ?? 0) > 0;
  const status = itinerary ? STATUS_META[itinerary.status] : null;
  const headerTitle = itinerary
    ? `Itinerary for ${itinerary.customerName ?? itinerary.leadReference ?? 'customer'}`
    : 'Itinerary';
  // Header shows THIS row's version (v1, v2, v3...) - matches the row's
  // position in the Revise/New Version chain. No more nextPublishVersion,
  // which was a lead-wide max+1 counter that produced confusing "v5" labels
  // on a row that was really v4.
  const headerVersion = itinerary?.revisionNumber ?? 1;

  // On first load, land on Days if the plan is already generated.
  useEffect(() => {
    if (!didInit.current && daysQuery.isSuccess) {
      didInit.current = true;
      if (hasDays) setStep('days');
    }
  }, [daysQuery.isSuccess, hasDays]);

  // ---- unsaved-changes guard ----
  // A step reports unsaved edits via onDirtyChange; we then intercept both
  // in-app navigation (router) AND step switches with our own confirm dialog,
  // plus the native beforeunload prompt for tab close / refresh.
  const [dirty, setDirty] = useState(false);
  // Autosave in-flight indicator. DaysTab reports it via `onSavingChange`
  // so the top bar can show a single "Saving..." pill instead of the tab
  // rendering its own bottom-sticky footer with Save/Discard buttons.
  const [saving, setSaving] = useState(false);
  const [pendingStep, setPendingStep] = useState<StepKey | null>(null);
  // Set true when Configure saves an edit (dates / rooms / ages); consumed by
  // DaysTab on its next mount to force a hotel re-price + reconcile for the new
  // config (Days remounts on the step switch, so an in-tab signal would be lost).
  const [forceReprice, setForceReprice] = useState(false);

  useEffect(() => setDirty(false), [id]); // reset when switching itineraries

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const guarding = blocker.state === 'blocked' || pendingStep !== null;
  const confirmLeave = (): void => {
    setDirty(false);
    if (pendingStep) {
      setStep(pendingStep);
      setPendingStep(null);
    }
    if (blocker.state === 'blocked') blocker.proceed();
  };
  const cancelLeave = (): void => {
    if (pendingStep) setPendingStep(null);
    if (blocker.state === 'blocked') blocker.reset();
  };

  // Steps after Configure unlock once days exist. Changes is admin-only and
  // only when this row has a parent version (v2+), even before days exist.
  // Logs is also admin-only and always available for any published itinerary
  // (so admins can see reprice history even on a stale/cancelled row).
  const hasParentVersion = Boolean(itinerary?.parentItineraryId);
  const canShowChanges = canSeeChanges && hasParentVersion;
  const canShowLogs = canSeeChanges;
  const visibleSteps = STEPS.filter((s) => {
    if (s.key === 'changes') return canShowChanges;
    if (s.key === 'logs') return canShowLogs;
    return true;
  });
  const stepEnabled = (key: StepKey): boolean => {
    if (key === 'configure') return true;
    if (key === 'changes') return canShowChanges;
    if (key === 'logs') return canShowLogs;
    return hasDays;
  };
  const currentIndex = visibleSteps.findIndex((s) => s.key === step);
  // Switching steps with unsaved edits routes through the same confirm.
  const attemptStep = (key: StepKey): void => {
    if (!stepEnabled(key) || key === step) return;
    if (dirty) setPendingStep(key);
    else setStep(key);
  };

  // Days + Preview render component prices through PriceVisibilityProvider:
  // non-admins see only what a price MOVED by, never the price itself. The
  // provider sits above both steps (and the TBO picker DaysTab opens) so they
  // share one session baseline.
  /**
   * A fixed departure's structure is contractual: its cities, days, hotels and
   * price all came from the template as one quoted package. The builder must
   * not offer the generic add / remove controls, or the itinerary silently
   * stops matching what the supplier priced. Enforced server-side too.
   */
  const isFixedDeparture = itinerary?.kind === 'FIXED_DEPARTURE';

  const renderStep = (): React.ReactNode => {
    if (!itinerary) return null;
    // Only the head of the revision chain can be edited. Past versions stay
    // viewable (including Configure) but every field + save action locks.
    const canEdit =
      itinerary.isLatestRevision &&
      (itinerary.status === 'DRAFT' || itinerary.status === 'IN_PROGRESS');
    switch (step) {
      case 'configure':
        // `key={itinerary.id}` forces a fresh mount when the operator switches
        // versions from the dropdown. ConfigureStep seeds every editable field
        // (departure city, hotel source, trip style, visa, insurance, legs
        // with check-in/out, flight legs with departure dates) via
        // `useState(() => ...)` initializers that only run on mount - without
        // remounting, only the initial version's values would stick and a
        // switch would leave stale state (most visibly the flight leg dates).
        return (
          <ConfigureStep
            key={itinerary.id}
            itinerary={itinerary}
            editable={canEdit}
            structureLocked={isFixedDeparture}
            onGenerated={() => setStep('days')}
            onDirtyChange={setDirty}
            onSaved={() => setForceReprice(true)}
          />
        );
      case 'changes':
        return <VersionDiffTab itineraryId={itinerary.id} />;
      case 'days':
        return (
          <DaysTab
            itineraryId={itinerary.id}
            editable={canEdit}
            structureLocked={isFixedDeparture}
            onDirtyChange={setDirty}
            onSavingChange={setSaving}
            forceReprice={forceReprice}
            onRepriceHandled={() => setForceReprice(false)}
          />
        );
      case 'inclusions':
        return (
          <InclusionsTab
            itineraryId={itinerary.id}
            editable={canEdit}
            structureLocked={isFixedDeparture}
          />
        );
      case 'pricing':
        return <PricingStep key={itinerary.id} itineraryId={itinerary.id} editable={canEdit} />;
      case 'preview':
        return <PreviewTab itineraryId={itinerary.id} onGoToPricing={() => setStep('pricing')} />;
      case 'logs':
        return <LogsTab itineraryId={itinerary.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - single-row command center. Version picker + details
          popovers keep the tab content flush; no tall cards pushing it down. */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={() =>
            navigate(
              from === 'lead' && itinerary?.leadId ? `/leads/${itinerary.leadId}` : '/itineraries',
            )
          }
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label={from === 'lead' && itinerary?.leadId ? 'Back to lead' : 'Back to itineraries'}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">
            {from === 'lead' && itinerary?.leadId ? 'Lead' : 'Itineraries'}
          </span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {headerTitle}
          </span>
        </div>
        <span className="flex-1" />
        {saving && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600"
            aria-live="polite"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" aria-hidden />
            Saving...
          </span>
        )}
        {itinerary && (
          <UzPopover
            side="bottom"
            align="end"
            className="w-[440px] p-0"
            open={versionsOpen}
            onOpenChange={setVersionsOpen}
            trigger={
              <button
                type="button"
                className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-medium text-zinc-700 tabular-nums transition-colors hover:bg-zinc-50"
              >
                v{headerVersion}
                {!itinerary.isLatestRevision && (
                  <span className="rounded bg-zinc-100 px-1 text-[10px] font-medium text-zinc-500">
                    past
                  </span>
                )}
                <ChevronDown className="h-3 w-3 text-zinc-400" aria-hidden />
              </button>
            }
          >
            <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
              <p className="text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
                Versions
              </p>
              <span className="text-[11px] text-zinc-400 tabular-nums">
                {versions.length} total
              </span>
            </div>
            {versionsQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Loading versions...
              </div>
            ) : versions.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-zinc-500">No versions yet.</p>
            ) : (
              <ul className="max-h-[420px] divide-y divide-zinc-100 overflow-y-auto">
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((v) => {
                    const paymentLink = v.paymentLink;
                    const paymentUrl = paymentLink?.paymentUrl ?? null;
                    const qrImageUrl = paymentLink?.qrImageUrl ?? null;
                    const currency = itinerary.currencyCode ?? 'INR';
                    const isViewing = v.itineraryId === itinerary.id;
                    const isSwitching = switchingToId === v.itineraryId;
                    return (
                      <li
                        key={v.itineraryId}
                        className={
                          (isViewing
                            ? 'bg-primary/5 border-l-primary border-l-2 '
                            : 'border-l-2 border-l-transparent ') +
                          (isSwitching ? 'opacity-70 ' : '') +
                          'transition-colors'
                        }
                      >
                        <button
                          type="button"
                          onClick={() => {
                            if (isViewing || isSwitching) {
                              setVersionsOpen(false);
                              return;
                            }
                            setSwitchingToId(v.itineraryId);
                            setVersionsOpen(false);
                            navigate(`/itineraries/${v.itineraryId}`, {
                              state: { freshVersion: true },
                            });
                          }}
                          disabled={isSwitching}
                          className={
                            'block w-full px-4 py-2.5 text-left ' +
                            (isViewing
                              ? 'cursor-default'
                              : 'cursor-pointer hover:bg-zinc-50 disabled:cursor-wait')
                          }
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                              {v.label}
                            </span>
                            {/* FD vs FIT: a version chain can hold both kinds on
                                one lead, so each version is labelled to stop the
                                two being confused. */}
                            <span
                              className={
                                'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ' +
                                (v.kind === 'FIXED_DEPARTURE'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-zinc-100 text-zinc-600')
                              }
                              title={
                                v.kind === 'FIXED_DEPARTURE'
                                  ? 'Fixed departure'
                                  : 'Flexible itinerary'
                              }
                            >
                              {v.kind === 'FIXED_DEPARTURE' ? 'FD' : 'FIT'}
                            </span>
                            {isViewing && (
                              <span className="bg-primary/10 text-primary inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                                <Check className="h-2.5 w-2.5" aria-hidden />
                                Viewing
                              </span>
                            )}
                            {isSwitching && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
                                <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />
                                Loading...
                              </span>
                            )}
                            <span className="flex-1" />
                            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700">
                              <span
                                aria-hidden
                                className={'h-1.5 w-1.5 rounded-full ' + STATUS_META[v.status].dot}
                              />
                              {STATUS_META[v.status].label}
                            </span>
                          </div>
                          <div className="mt-1 flex items-center gap-2 text-[11px] text-zinc-500 tabular-nums">
                            {v.quoteId ? (
                              <span title="Quote No.">{v.quoteId}</span>
                            ) : (
                              <span className="text-zinc-400">Not published</span>
                            )}
                            <span className="text-zinc-300">|</span>
                            <span className="font-medium text-zinc-700">
                              {fmtPaise(v.grandTotalPaise, currency)}
                            </span>
                            <span className="flex-1" />
                            <span>{v.publishedAt ? fmtDateTime(v.publishedAt) : 'Draft'}</span>
                          </div>
                        </button>
                        {v.isPublished && (
                          <div className="flex items-center gap-1 border-t border-zinc-100/70 bg-zinc-50/40 px-3 py-1.5">
                            {v.shareUrl && (
                              <button
                                type="button"
                                onClick={() => copyVersionLink(v.shareUrl!)}
                                className="inline-flex h-6 cursor-pointer items-center gap-1 rounded px-1.5 text-[11px] text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                                title="Copy share link"
                              >
                                <Link2 className="h-3 w-3" aria-hidden />
                                Share
                              </button>
                            )}
                            {paymentUrl && (
                              <button
                                type="button"
                                onClick={() =>
                                  copyToClipboard(
                                    paymentUrl,
                                    paymentLink?.kind === 'instalment' && paymentLink.label
                                      ? `${paymentLink.label} link copied`
                                      : 'Payment link copied',
                                  )
                                }
                                className="inline-flex h-6 cursor-pointer items-center gap-1 rounded px-1.5 text-[11px] text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                                title={
                                  paymentLink?.kind === 'instalment' && paymentLink.label
                                    ? `Copy ${paymentLink.label} link`
                                    : 'Copy payment link'
                                }
                              >
                                <CreditCard className="h-3 w-3" aria-hidden />
                                {paymentLink?.kind === 'instalment' && paymentLink.seq
                                  ? `I${paymentLink.seq} link`
                                  : 'Pay link'}
                              </button>
                            )}
                            {qrImageUrl && (
                              <UzPopover
                                side="right"
                                align="start"
                                trigger={
                                  <button
                                    type="button"
                                    className="inline-flex h-6 cursor-pointer items-center gap-1 rounded px-1.5 text-[11px] text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                                    title="Show payment QR"
                                  >
                                    <QrCode className="h-3 w-3" aria-hidden />
                                    QR
                                  </button>
                                }
                              >
                                <div className="flex flex-col items-center gap-1 p-3">
                                  <img src={qrImageUrl} alt="Scan to pay" className="h-40 w-40" />
                                  <p className="text-[10px] text-zinc-500">Scan to pay</p>
                                </div>
                              </UzPopover>
                            )}
                            <span className="flex-1" />
                            {paymentLink && (
                              <>
                                {paymentLink.kind !== 'instalment' &&
                                  paymentLink.cashDiscountPaise &&
                                  paymentLink.cashDiscountPaise !== '0' && (
                                    <UzBadge tone="success" className="h-4 px-1.5 text-[10px]">
                                      Cash discount{' '}
                                      {fmtPaise(paymentLink.cashDiscountPaise, currency)}
                                    </UzBadge>
                                  )}
                                {paymentLink.status === 'PAID' && (
                                  <UzBadge tone="success" className="h-4 px-1.5 text-[10px]">
                                    Paid
                                  </UzBadge>
                                )}
                                {paymentLink.status === 'ACTIVE' && (
                                  <span className="text-[10px] text-zinc-500">Link ready</span>
                                )}
                                {paymentLink.status === 'PENDING' && (
                                  <span className="text-[10px] text-zinc-400">Generating...</span>
                                )}
                                {paymentLink.status === 'FAILED' && (
                                  <UzBadge tone="destructive" className="h-4 px-1.5 text-[10px]">
                                    Link failed
                                  </UzBadge>
                                )}
                                {paymentLink.status === 'LAPSED' && (
                                  <UzBadge tone="warning" className="h-4 px-1.5 text-[10px]">
                                    Window closed
                                  </UzBadge>
                                )}
                                {paymentLink.status === 'CANCELLED' && (
                                  <span className="text-[10px] text-zinc-400">Cancelled</span>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
              </ul>
            )}
            {/* "New version" can be triggered from ANY non-terminal version -
                customer might say "we liked v3, small tweak" while v5 is the
                current latest. Server computes the new revision number as
                MAX+1 for the lead so no collision, and copies forward from
                whichever version the operator was viewing. Terminal statuses
                (CANCELLED / CONVERTED_TO_BOOKING) still 400 on the server. */}
            {itinerary.status !== 'CANCELLED' && itinerary.status !== 'CONVERTED_TO_BOOKING' && (
              <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-3 py-2">
                <UzButton size="sm" onClick={handleNewVersion} loading={createVersion.isPending}>
                  <GitBranchPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {itinerary.isLatestRevision
                    ? 'New version'
                    : `New version from v${itinerary.revisionNumber}`}
                </UzButton>
              </div>
            )}
          </UzPopover>
        )}
        {status && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
        )}
        {itinerary &&
          (itinerary.status === 'ACCEPTED' || itinerary.status === 'CONVERTED_TO_BOOKING') && (
            <RequestVoidButton itineraryId={itinerary.id} />
          )}
        {itinerary && (
          <UzPopover
            side="bottom"
            align="end"
            className="w-[280px] p-0"
            trigger={
              <button
                type="button"
                className="-mr-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
                aria-label="Itinerary details"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </button>
            }
          >
            <div className="border-b border-zinc-100 px-4 py-2.5">
              <p className="text-xs font-semibold tracking-wider text-zinc-500 uppercase">
                Details
              </p>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-4 py-3 text-sm">
              <dt className="text-zinc-500">Sales owner</dt>
              <dd className="font-medium text-zinc-900">
                {itinerary.assignedAgentName ?? 'Unassigned'}
              </dd>
              <dt className="text-zinc-500">Kind</dt>
              <dd className="font-medium text-zinc-900">{KIND_LABEL[itinerary.kind]}</dd>
              <dt className="text-zinc-500">Created</dt>
              <dd className="font-medium text-zinc-900">{fmtDate(itinerary.createdAt)}</dd>
              {itinerary.leadReference && (
                <>
                  <dt className="text-zinc-500">Lead</dt>
                  <dd>
                    <Link
                      to={itinerary.leadId ? `/leads/${itinerary.leadId}` : '#'}
                      rel="noopener noreferrer"
                      className="text-primary font-medium hover:underline"
                    >
                      {itinerary.leadReference}
                    </Link>
                  </dd>
                </>
              )}
              {itinerary.legacyAGId && (
                <>
                  <dt className="text-zinc-500">Legacy ID</dt>
                  <dd className="font-mono text-zinc-700 tabular-nums">{itinerary.legacyAGId}</dd>
                </>
              )}
            </dl>
          </UzPopover>
        )}
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {detailQuery.isLoading && (
          <div className="space-y-4">
            <div className="h-5 w-2/3 animate-pulse rounded bg-zinc-100" />
            <div className="h-24 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
            <div className="h-64 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
          </div>
        )}
        {detailQuery.isError && (
          <UzAlert tone="destructive">
            Failed to load the itinerary. It may have been archived or deleted.
          </UzAlert>
        )}

        {itinerary && (
          <>
            {/* Note: no more "client viewing v1 - unpublished changes" banner.
                Under the new model, Revise mints a fresh row so the row you
                edit is always a distinct version - never a mix of old+new. */}

            {/* Stepper rail - sticky flush against the top breadcrumb bar (h-12).
                Top offset matches the breadcrumb bar's sticky-bottom to avoid a
                visible gap between the two bars:
                  default: bar -top-6 (-24) + h-12 => bottom at 24 => stepper top-6
                  lg:      bar -top-8 (-32) + h-12 => bottom at 16 => stepper top-4
                Full-width bleed via negative side margins and matching padding
                keeps the underline flush with the container. */}
            <div className="sticky top-6 z-20 -mx-6 mb-6 border-b border-zinc-200 bg-white px-6 pt-2 pb-2 lg:top-4 lg:-mx-8 lg:px-8">
              <div className="flex items-center justify-between gap-3">
                <ol className="flex min-w-0 flex-wrap items-center gap-1.5">
                  {visibleSteps.map((s, i) => {
                    const enabled = stepEnabled(s.key);
                    const active = s.key === step;
                    const done = enabled && i < currentIndex;
                    return (
                      <li key={s.key} className="flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={!enabled}
                          onClick={() => attemptStep(s.key)}
                          className={
                            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
                            (active
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : enabled
                                ? 'hover:bg-accent hover:text-accent-foreground text-zinc-700'
                                : 'cursor-not-allowed text-zinc-300')
                          }
                        >
                          <span
                            className={
                              'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ' +
                              (active
                                ? 'text-primary-foreground bg-white/25'
                                : done
                                  ? 'bg-primary/10 text-primary'
                                  : enabled
                                    ? 'bg-zinc-100 text-zinc-600'
                                    : 'bg-zinc-100 text-zinc-300')
                            }
                          >
                            {!enabled ? (
                              <Lock className="h-3 w-3" aria-hidden />
                            ) : done ? (
                              <Check className="h-3 w-3" aria-hidden />
                            ) : (
                              i + 1
                            )}
                          </span>
                          {s.label}
                        </button>
                        {i < visibleSteps.length - 1 && (
                          <span className="text-zinc-300" aria-hidden>
                            -
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
                {!itinerary.isLatestRevision && (
                  <span
                    className="inline-flex shrink-0 items-center rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold tracking-wide text-amber-800 uppercase"
                    title="This is a previous version. Switch to the latest to edit."
                  >
                    View only
                  </span>
                )}
              </div>
            </div>

            <PriceVisibilityProvider itineraryId={itinerary.id}>
              {renderStep()}
            </PriceVisibilityProvider>
          </>
        )}
      </div>

      {/* Custom unsaved-changes confirm (replaces the browser dialog for in-app
          navigation + step switches). */}
      <UzDialog
        open={guarding}
        onOpenChange={(open) => {
          if (!open) cancelLeave();
        }}
        title="Discard unsaved changes?"
        description="You have changes in this itinerary that haven't been saved. Leaving now will lose them."
        footer={
          <div className="flex justify-end gap-2">
            <UzButton variant="ghost" onClick={cancelLeave}>
              Stay on page
            </UzButton>
            <UzButton variant="destructive" onClick={confirmLeave}>
              Discard &amp; leave
            </UzButton>
          </div>
        }
      >
        <span />
      </UzDialog>

      {/* Blank vs copy-forward choice for "New version". */}
      <UzDialog
        open={newVersionDialogOpen}
        onOpenChange={(open) => {
          if (!open) setNewVersionDialogOpen(false);
        }}
        title="Create a new version"
        description="The current version stays accessible, and the new one becomes the latest. Start it blank as a fresh FIT (custom) itinerary, or copy the current version forward - keeping its plan, days, pricing and type (including Fixed Departure)."
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => setNewVersionDialogOpen(false)}
              disabled={createVersion.isPending}
            >
              Cancel
            </UzButton>
            <UzButton
              variant="outline"
              onClick={() => startNewVersion(false)}
              loading={createVersion.isPending}
            >
              Start blank
            </UzButton>
            <UzButton onClick={() => startNewVersion(true)} loading={createVersion.isPending}>
              Copy current version
            </UzButton>
          </div>
        }
      >
        <span />
      </UzDialog>
    </div>
  );
};
